import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, api, clearToken, getSelf, getToken } from '../services/client';
import type { ClientListItem, PunchRecord } from '../services/types';
import { BilingualText } from '../components/BilingualText';
import { BilingualButton } from '../components/BilingualButton';
import { BrandHeader } from '../components/BrandHeader';
import { dict } from '../i18n/dictionary';
import { formatClientName } from '../utils/formatClientName';

function formatTime(iso: string): string {
  const d = new Date(iso);
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

/** 名簿一本化により打刻は事務所管理へ移行。打刻ボタンを無効化し案内を表示する。 */
const PUNCH_DISABLED = true;

export function Punch() {
  const navigate = useNavigate();
  const self = getSelf();
  const [clients, setClients] = useState<ClientListItem[]>([]);
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [today, setToday] = useState<PunchRecord[]>([]);
  const [pending, setPending] = useState<'in' | 'out' | null>(null);
  const [errorMsg, setErrorMsg] = useState<{ ja: string; vi: string } | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [c, t] = await Promise.all([api.listClients(), api.todayPunches()]);
      setClients(c);
      if (c.length > 0 && c[0] && !selectedClient) setSelectedClient(c[0].id);
      setToday(t);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        // services/client.ts 側で既に `/?expired=1` にリダイレクト済みのため
        // 追加の navigate は不要。ローカル token のみクリア。
        clearToken();
        return;
      }
      setErrorMsg({ ja: dict.networkError.ja, vi: dict.networkError.vi });
    }
  }, [selectedClient]);

  useEffect(() => {
    if (!getToken() || !self) {
      navigate('/');
      return;
    }
    void refresh();
  }, [navigate, refresh, self]);

  const onPunch = async (punchType: 'in' | 'out'): Promise<void> => {
    if (!selectedClient || pending) return;
    setPending(punchType);
    setErrorMsg(null);
    try {
      await api.punch(selectedClient, punchType);
      navigate(`/punch/success/${punchType}`);
    } catch (e) {
      if (e instanceof ApiError) {
        const body = e.body?.error;
        if (body?.code === 'DUPLICATE_PUNCH') {
          setErrorMsg({
            ja:
              punchType === 'in' ? dict.alreadyPunchedIn.ja : dict.alreadyPunchedOut.ja,
            vi:
              punchType === 'in' ? dict.alreadyPunchedIn.vi : dict.alreadyPunchedOut.vi,
          });
        } else if (e.status === 401) {
          // services/client.ts 側で既に `/?expired=1` にリダイレクト済み
          clearToken();
          return;
        } else {
          setErrorMsg({
            ja: body?.message_ja ?? dict.error.ja,
            vi: body?.message_vi ?? dict.error.vi,
          });
        }
      } else {
        setErrorMsg({ ja: dict.networkError.ja, vi: dict.networkError.vi });
      }
    } finally {
      setPending(null);
    }
  };

  // 退勤打刻リマインド: 今日「出勤」したのに同じ現場の「退勤」が無い場合に表示。
  // (現場ごとに in 件数 > out 件数 なら在場中とみなす)
  const openClientIds = (() => {
    const ins = new Map<string, number>();
    const outs = new Map<string, number>();
    for (const p of today) {
      const m = p.punch_type === 'in' ? ins : outs;
      m.set(p.client_id, (m.get(p.client_id) ?? 0) + 1);
    }
    const open: string[] = [];
    for (const [cid, n] of ins) {
      if (n > (outs.get(cid) ?? 0)) open.push(cid);
    }
    return open;
  })();
  const needsClockOut = openClientIds.length > 0;

  if (!self) return null;

  return (
    <main className="page page-punch">
      <BrandHeader />
      <header className="page-header punch-header">
        <div className="employee-display">
          <div className="name-ja">{self.name_ja}</div>
          {self.name_vi && <div className="name-vi">{self.name_vi}</div>}
        </div>
        <div className="header-actions">
          <button
            className="link-logout"
            onClick={() => navigate('/change-pin')}
            style={{ marginRight: 8 }}
          >
            <BilingualText value={dict.menuChangePin} separator="slash" />
          </button>
          <button
            className="link-logout"
            onClick={() => {
              clearToken();
              navigate('/');
            }}
          >
            <BilingualText value={dict.logout} separator="slash" />
          </button>
        </div>
      </header>

      {PUNCH_DISABLED && (
        <div className="error-banner" role="status" style={{ background: '#e3f2fd', color: '#0d47a1' }}>
          <span className="bi-ja">打刻は不要になりました。出退勤は事務所で管理します。</span>
          <span className="bi-vi">Không cần chấm công nữa. Văn phòng sẽ quản lý chấm công.</span>
        </div>
      )}

      <button
        className="btn btn-primary btn-lg"
        style={{ width: '100%', margin: '12px 0' }}
        onClick={() => navigate('/nenmatsu')}
      >
        <span className="bi-ja">📝 年末調整の申告</span>
        <span className="bi-vi">Khai báo điều chỉnh thuế cuối năm</span>
      </button>

      {!PUNCH_DISABLED && (
        <section className="site-picker">
          <label className="site-label">
            <BilingualText value={dict.selectSite} />
          </label>
          <select
            className="site-select"
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
          >
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {formatClientName(c.name, c.name_en)}
              </option>
            ))}
          </select>
        </section>
      )}

      {errorMsg && (
        <div className="error-banner" role="alert">
          <span className="bi-ja">{errorMsg.ja}</span>
          <span className="bi-vi">{errorMsg.vi}</span>
        </div>
      )}

      {needsClockOut && !errorMsg && (
        <div className="clockout-reminder" role="status">
          <span className="reminder-icon" aria-hidden="true">⏰</span>
          <span className="reminder-text">
            <BilingualText value={dict.clockOutReminder} />
          </span>
        </div>
      )}

      {!PUNCH_DISABLED && (
        <div className="punch-actions">
          <BilingualButton
            label={dict.punchIn}
            variant="success"
            size="lg"
            icon="🌅"
            onClick={() => onPunch('in')}
            disabled={!selectedClient || pending !== null}
          />
          <BilingualButton
            label={dict.punchOut}
            variant="danger"
            size="lg"
            icon="🌙"
            onClick={() => onPunch('out')}
            disabled={!selectedClient || pending !== null}
          />
        </div>
      )}

      <section className="today-section">
        <h3>
          <BilingualText value={dict.todayPunches} />
        </h3>
        {today.length === 0 ? (
          <p className="empty">
            <BilingualText value={dict.noPunchesToday} />
          </p>
        ) : (
          <ul className="today-list">
            {today.map((p) => (
              <li key={p.id} className={`today-item type-${p.punch_type}`}>
                <span className="time">{formatTime(p.punched_at)}</span>
                <span className="type">
                  <BilingualText
                    value={p.punch_type === 'in' ? dict.punchedAtIn : dict.punchedAtOut}
                    separator="slash"
                  />
                </span>
                <span className="site">
                  {formatClientName(p.client_name, p.client_name_en)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}
